<template>
  <div>
    <page-header
      :actions="headerActionsConfig"
      left-side-class="col-12 col-md-7"
      :page="headerConfig"
      right-side-class="col-12 col-md-5"
    />
    <div v-if="contractInfo">
      <div class="mb-3 mt-n2">
        <div class="font-24 font-weight-bold">
          {{ contractTypeName }}
        </div>
        <div class="d-flex align-items-center">
          <em
            class="fa fa-circle font-11 mr-2"
            :class="`color-${statusColor}`"
          />
          <span class="font-16 font-weight-bold">
            {{ statusName | empty }}
          </span>
        </div>
      </div>
      <div v-if="!isReadOnly" class="toolbar-actions mt-n3 mt-md-n5">
        <button
          :disabled="showHeader"
          title="Mostrar / Ocultar Encabezado"
          @click="handleToggleHeader"
        >
          <img src="@/assets/img/editor-header.svg" style="height: 16px;" />
          <span class="ml-2">Agregar Encabezado</span>
        </button>
        <button
          :disabled="showFooter"
          title="Mostrar / Ocultar Pie de Página"
          @click="handleToggleFooter"
        >
          <img src="@/assets/img/editor-footer.svg" style="height: 16px;" />
          <span class="ml-2">Agregar Pie de Página</span>
        </button>
      </div>
      <div v-if="hasTokens" class="editor-container">
        <div class="document-editor-wrapper">
          <div class="document-builder">
            <div
              v-if="tokenErrors.length"
              class="token-error-panel alert alert-danger mt-2"
            >
              <b>Se encontraron errores en tokens:</b>
              <ul>
                <li v-for="(err, idx) in tokenErrors" :key="idx">
                  {{ err.message }}
                </li>
              </ul>
            </div>
            <div
              v-show="showHeader"
              class="document-section header-section"
              @click="setActiveEditor('header')"
            >
              <div :class="{ 'editor-active': activeEditor === 'header' }">
                <vue-editor
                  id="headerEditor"
                  ref="quillEditorHeaderRef"
                  v-model="headerContent"
                  class="editor-minimal"
                  :disabled="isReadOnly"
                  :editor-options="minimalEditorOptions"
                  use-table-better
                  @ready="onHeaderEditorReady"
                />
                <div class="section-label">
                  Encabezado
                  <action-button
                    v-if="!isReadOnly"
                    id="btn-delete-header"
                    i="fa fa-times-circle"
                    text="Eliminar"
                    variant="outline-danger"
                    @click="handleDeleteHeader"
                  />
                </div>
              </div>
            </div>
            <div
              class="document-section content-section mt-2"
              @click="setActiveEditor('content')"
            >
              <div :class="{ 'editor-active': activeEditor === 'content' }">
                <vue-editor
                  id="contentEditor"
                  ref="quillEditorContentRef"
                  v-model="documentContent"
                  class="editor-extended"
                  :disabled="isReadOnly"
                  :editor-options="contentEditorOptions"
                  use-table-better
                  @ready="onContentEditorReady"
                />
              </div>
            </div>
            <div
              v-show="showFooter"
              class="document-section footer-section"
              @click="setActiveEditor('footer')"
            >
              <div :class="{ 'editor-active': activeEditor === 'footer' }">
                <vue-editor
                  id="footerEditor"
                  ref="quillEditorFooterRef"
                  v-model="footerContent"
                  class="editor-minimal"
                  :disabled="isReadOnly"
                  :editor-options="minimalEditorOptions"
                  use-table-better
                  @ready="onFooterEditorReady"
                />
                <div class="section-label">
                  Pie de Página
                  <action-button
                    v-if="!isReadOnly"
                    id="btn-delete-footer"
                    i="fa fa-times-circle"
                    text="Eliminar"
                    variant="outline-danger"
                    @click="handleDeleteFooter"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <token-sidebar
          v-if="hasTokens && !isReadOnly"
          :tokens="tokens"
          @insert-token="insertToken"
        />
      </div>
    </div>
  </div>
</template>

<script>
// third party
import VueEditor from '@/components/common/custom-quill-editor/Index.vue';
import Quill from 'quill';

// libs
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';

// mixins
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';

// services
import {
  saveContractEditor as _saveContractEditor,
  getContractEditor as _getContractEditor,
  getContractEditorPreview as _getContractEditorPreview,
  publishVersion as _publishVersion,
} from '@/services/provider/contract-editor.api.js';

// child components
import { TokenSidebar } from './components';

// utils
import utils from '@/utils/utilities';
import TokenBlot, {
  addTokenEnterHandler,
} from '@/utils/quill-custom-blot/token.blot.js';
import SelloBlot, {
  addSelloHandler,
} from '@/utils/quill-custom-blot/sello.blot.js';
import UnderlineBelowBlot, {
  addUnderlineBelowHandler,
} from '@/utils/quill-custom-blot/underline-below.blot';

// constants
import rpContractEditorVersionStatus, {
  rpContractEditorVersionStatusColor,
} from '@/utils/constants/rpContractEditorVersionStatus.js';
import restrictedTokens from '@/utils/constants/contractEditorRestrictedTokens';

Quill.register(TokenBlot);
Quill.register(SelloBlot);
Quill.register(UnderlineBelowBlot);

export default {
  name: 'ContractEditorMain',
  mixins: [alertMixin],
  components: {
    VueEditor,
    PageHeader,
    ActionButton,
    TokenSidebar,
  },
  async beforeRouteLeave(to, from, next) {
    if (this.skipRouteGuard) {
      next();
      return;
    }
    if (this.hasUnsavedChanges) {
      const answer = await this.ShowCancelModal(
        'Cambios sin Guardar',
        '¿Está seguro que desea salir? Hay cambios sin guardar que se perderán.'
      );
      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
  data: () => ({
    quillEditorHeaderRef: undefined,
    quillEditorContentRef: undefined,
    quillEditorFooterRef: undefined,
    activeEditor: 'content',
    showHeader: false,
    showFooter: false,
    contractInfo: undefined,
    headerContent: '',
    footerContent: '',
    documentContent: '',
    tokens: [],
    tokenErrors: [],
    originalHeaderContent: '',
    originalFooterContent: '',
    originalDocumentContent: '',
    originalShowHeader: false,
    originalShowFooter: false,
    skipRouteGuard: false,
    editorsReady: {
      header: false,
      content: false,
      footer: false,
    },
  }),
  computed: {
    headerConfig() {
      return {
        title: 'Editor de Contratos',
        icon: 'fas fa-file-signature',
        routes: ['Inicio', 'USPQRP', 'Editor de Contratos'],
      };
    },
    headerActionsConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left fa-fw',
          variant: 'outline-success',
          click: () => this.handleGoBack(),
        },
        {
          text: 'Previsualizar',
          icon: 'fas fa-eye mr-2',
          variant: 'outline-success',
          visible: true,
          click: () => this.getContractEditorPreview(),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save mr-2',
          variant: 'outline-success',
          visible: this.isVersionDraft,
          click: () => this.handleSaveDocumentBuilder(),
        },
        {
          text: 'Publicar',
          icon: 'fas fa-flag mr-2',
          variant: 'success',
          visible: this.isVersionDraft,
          click: () => this.handlePublish(),
        },
      ];
    },
    minimalEditorOptions() {
      return {
        modules: {
          toolbar: [
            [{ header: [false, 1, 2, 3, 4, 5, 6] }],
            ['bold', 'italic', 'underline', 'strike'],
            [
              { align: '' },
              { align: 'center' },
              { align: 'right' },
              { align: 'justify' },
            ],
            ['table-better'],
            ['clean'],
          ],
        },
      };
    },
    contentEditorOptions() {
      return {
        theme: 'snow',
        modules: {
          toolbar: {
            container: [
              [{ header: [false, 1, 2, 3, 4, 5, 6] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ script: 'sub' }, { script: 'super' }],
              [
                { align: '' },
                { align: 'center' },
                { align: 'right' },
                { align: 'justify' },
              ],
              [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
              [{ indent: '-1' }, { indent: '+1' }],
              [{ color: [] }, { background: [] }],
              ['image'],
              ['table-better'],
              ['underline-below'],
              ['sello'],
              ['clean'],
            ],
            handlers: {
              'underline-below': this.underlineBelowHandler,
            },
          },
        },
      };
    },
    academicYearId() {
      return this.$route.params.academicYearId;
    },
    contractTypeId() {
      return this.$route.params.contractTypeId;
    },
    contractVersionId() {
      return this.$route.params?.versionId || null;
    },
    contractTypeName() {
      return `Contrato de ${this.contractInfo?.contractTypeName}`;
    },
    statusId() {
      return this.contractInfo?.statusId;
    },
    statusName() {
      return this.contractInfo?.statusName;
    },
    statusColor() {
      return (
        {
          [rpContractEditorVersionStatus.Draft]:
            rpContractEditorVersionStatusColor[
              rpContractEditorVersionStatus.Draft
            ],
          [rpContractEditorVersionStatus.Published]:
            rpContractEditorVersionStatusColor[
              rpContractEditorVersionStatus.Published
            ],
          [rpContractEditorVersionStatus.Inactive]:
            rpContractEditorVersionStatusColor[
              rpContractEditorVersionStatus.Inactive
            ],
        }[this.statusId] || null
      );
    },
    isVersionPublished() {
      return (
        this.contractInfo?.statusId === rpContractEditorVersionStatus.Published
      );
    },
    isVersionDraft() {
      return (
        this.contractInfo?.statusId === rpContractEditorVersionStatus.Draft
      );
    },
    isReadOnly() {
      return [
        rpContractEditorVersionStatus.Published,
        rpContractEditorVersionStatus.Inactive,
      ].includes(this.contractInfo?.statusId);
    },
    activeEditorLabel() {
      const labels = {
        header: 'Encabezado',
        content: 'Contenido Principal',
        footer: 'Pie de Página',
      };
      return labels[this.activeEditor] || '';
    },
    hasTokens() {
      return this.tokens?.length;
    },
    hasErrorInTokens() {
      return this.tokenErrors?.length > 0;
    },
    hasUnsavedChanges() {
      if (this.isReadOnly) return false;
      return (
        this.headerContent != this.originalHeaderContent ||
        this.footerContent != this.originalFooterContent ||
        this.documentContent != this.originalDocumentContent ||
        this.showHeader != this.originalShowHeader ||
        this.showFooter != this.originalShowFooter
      );
    },
  },
  async mounted() {
    await this.getDocumentBuilder();
  },
  methods: {
    setActiveEditor(editor) {
      this.activeEditor = editor;
      this.$nextTick(() => {
        let targetQuill;
        switch (editor) {
          case 'header':
            targetQuill = this.quillEditorHeaderRef;
            break;
          case 'footer':
            targetQuill = this.quillEditorFooterRef;
            break;
          case 'content':
          default:
            targetQuill = this.quillEditorContentRef;
            break;
        }
        if (targetQuill) {
          targetQuill.focus();
        }
      });
    },
    handleToggleHeader() {
      this.showHeader = !this.showHeader;
      if (this.showHeader && this.activeEditor !== 'header') {
        this.$nextTick(() => {
          this.setActiveEditor('header');
        });
      }
    },
    handleToggleFooter() {
      this.showFooter = !this.showFooter;
      if (this.showFooter && this.activeEditor !== 'footer') {
        this.$nextTick(() => {
          this.setActiveEditor('footer');
        });
      }
    },
    async handleSaveDocumentBuilder() {
      if (this.hasErrorInTokens) {
        this.ShowToast(
          'Alerta',
          'No se pueden guardar los cambios ya que existen errores en el formato de los tokens.',
          'error'
        );
        return;
      }
      const payload = {
        headerHtml: this.headerContent,
        contentHtml: this.documentContent,
        footerHtml: this.footerContent,
        hasHeader: this.showHeader,
        hasFooter: this.showFooter,
        academicYearId: this.academicYearId,
        contractTypeId: this.contractTypeId,
        versionId: this.contractVersionId,
      };
      const response = await _saveContractEditor(payload).catch((err) => {
        this.ShowToast('Alerta', err.response.data.message || 'error', 'error');
      });
      if (response?.data) {
        const responseData = response.data;
        this.ShowToast(
          'Proceso Exitoso.',
          'El documento ha sido guardado exitosamente.',
          'success'
        );
        this.saveOriginalState();
        if (responseData.isNew) {
          this.$router.replace({
            name: 'contractEditorManagement',
            params: {
              academicYearId: this.academicYearId,
              contractTypeId: this.contractTypeId,
              versionId: responseData.versionId,
            },
          });
        }
      }
    },
    async handlePublish() {
      if (this.hasErrorInTokens) {
        this.ShowToast(
          'Alerta',
          'No se pueden guardar los cambios ya que existen errores en el formato de los tokens.',
          'error'
        );
        return;
      }
      const payload = {
        academicYearId: this.academicYearId,
        contractTypeId: this.contractTypeId,
        versionId: this.contractVersionId,
      };
      const answer = await this.ShowCancelModal(
        'Atención',
        `¿Está seguro que desea publicar esta versión?`,
        'Sí',
        'No'
      );
      if (!answer) return;
      const response = await _publishVersion(payload).catch((err) => {
        this.ShowToast('Alerta', err.response.data.message, 'error');
      });
      if (response?.data) {
        this.ShowToast(
          'Proceso exitoso',
          'La versión ha sido publicada existosamente.',
          'success'
        );
        await this.getDocumentBuilder();
      }
    },
    async getDocumentBuilder() {
      const response = await _getContractEditor(
        this.academicYearId,
        this.contractTypeId,
        this.contractVersionId
      ).catch((err) => {
        this.ShowToast('Alerta', err.response.data.message || 'error', 'error');
      });
      const responseData = response?.data;
      if (responseData) {
        this.contractInfo = responseData;
        this.tokens = responseData.tokens;
        this.headerContent = responseData.headerHtml || '';
        this.documentContent = responseData.contentHtml || '';
        this.footerContent = responseData.footerHtml || '';
        this.showHeader = responseData.hasHeader || false;
        this.showFooter = responseData.hasFooter || false;
      }
    },
    async getContractEditorPreview() {
      if (this.hasErrorInTokens) {
        this.ShowToast(
          'Alerta',
          'No se pueden guardar los cambios ya que existen errores en el formato de los tokens.',
          'error'
        );
        return;
      }
      const payload = {
        academicYearId: this.academicYearId,
        contractTypeId: this.contractTypeId,
        versionId: this.contractVersionId,
        headerHtml: this.headerContent,
        contentHtml: this.documentContent,
        footerHtml: this.footerContent,
      };
      const response = await _getContractEditorPreview(payload).catch((err) => {
        this.ShowToast(
          'Alerta',
          err.response.data.message || 'error',
          'error',
          undefined,
          10000
        );
      });
      if (response?.data) {
        utils.convertToBlobWindow(response.data);
      }
    },
    filterTokens() {},
    quillInstance() {
      const editorMap = {
        header: this.quillEditorHeaderRef,
        footer: this.quillEditorFooterRef,
        content: this.quillEditorContentRef,
      };

      return editorMap[this.activeEditor] || this.quillEditorContentRef;
    },
    insertToken(token) {
      const currentQuill = this.quillInstance();
      if (!currentQuill) return;

      const pageTokens = ['_numero_pagina', '_total_pagina'];
      if (pageTokens.includes(token.name) && this.activeEditor === 'content') {
        this.ShowToast(
          'Token Restringido',
          `El token <b>${token.name}</b> solo puede agregarse en el Encabezado o Pie de Página.`,
          'error'
        );
        return;
      }

      const range = currentQuill.getSelection(true);
      const index = range ? range.index : currentQuill.getLength();

      currentQuill.insertText(index, ' ', {}, 'user');

      if (token.template) {
        currentQuill.insertText(index, token.template, {}, 'user');
        const cursorPos = token.template.indexOf('\n') + 1 + index;
        currentQuill.setSelection(cursorPos, 0);
      } else {
        currentQuill.insertEmbed(
          index + 1,
          'token',
          { token: token.name, dataType: token.dataType.trim().toLowerCase() },
          'user'
        );
        const tokenLength = `{{${token.name}}}`.length;
        currentQuill.setSelection(index + 1 + tokenLength + 1);
      }

      currentQuill.focus();
    },
    async onHeaderEditorReady(quill) {
      this.quillEditorHeaderRef = quill;
      addTokenEnterHandler(quill);
      this.editorsReady.header = true;
      this.checkAllEditorsReady();
    },
    async onContentEditorReady(editor) {
      const quill = editor.quill || editor;
      this.quillEditorContentRef = quill;

      quill.root.setAttribute('spellcheck', 'false');
      quill.root.setAttribute('autocorrect', 'off');
      quill.root.setAttribute('autocomplete', 'off');
      quill.root.setAttribute('autocapitalize', 'off');

      addTokenEnterHandler(quill);
      addSelloHandler(quill);
      addUnderlineBelowHandler(quill);

      this.$nextTick(() => {
        const selloBtn = this.$el.querySelector('.ql-sello');
        if (selloBtn) {
          selloBtn.innerHTML = '✅';
          selloBtn.title = 'Insertar sello de aprobación';
        }
      });

      this.$nextTick(() => {
        const underlineBelowBtn = this.$el.querySelector('.ql-underline-below');
        if (underlineBelowBtn) {
          underlineBelowBtn.innerHTML = `
            <svg viewBox="0 0 18 18" style="width: 18px; height: 18px;">
              <line x1="5" y1="5" x2="13" y2="5" stroke="currentColor" stroke-width="1.2"/>
              <line x1="5" y1="8" x2="13" y2="8" stroke="currentColor" stroke-width="1.2"/>
              <line x1="3" y1="14" x2="15" y2="14" stroke="currentColor" stroke-width="3"/>
            </svg>
          `;
          underlineBelowBtn.title = 'Texto con línea debajo';
        }
      });

      quill.on('text-change', () => {
        this.$nextTick(() => {
          this.validateMalformatedTokens();
        });
      });

      this.$nextTick(() => {
        this.validateMalformatedTokens();
      });

      this.editorsReady.content = true;
      this.checkAllEditorsReady();
    },
    async onFooterEditorReady(quill) {
      this.quillEditorFooterRef = quill;
      addTokenEnterHandler(quill);
      this.editorsReady.footer = true;
      this.checkAllEditorsReady();
    },
    handleDeleteHeader() {
      this.headerContent = undefined;
      this.showHeader = false;
    },
    handleDeleteFooter() {
      this.footerContent = undefined;
      this.showFooter = false;
    },
    underlineBelowHandler() {
      const quill = this.quillInstance();
      const range = quill.getSelection();
      if (range) {
        const format = quill.getFormat(range);
        quill.formatText(
          range.index,
          range.length,
          'underline-below',
          !format['underline-below']
        );
      }
    },
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },
    saveOriginalState() {
      this.originalHeaderContent = this.headerContent || '';
      this.originalFooterContent = this.footerContent || '';
      this.originalDocumentContent = this.documentContent || '';
      this.originalShowHeader = this.showHeader || false;
      this.originalShowFooter = this.showFooter || false;
    },
    checkAllEditorsReady() {
      const contentReady = this.editorsReady.content;
      const headerReady = !this.showHeader || this.editorsReady.header;
      const footerReady = !this.showFooter || this.editorsReady.footer;
      if (contentReady && headerReady && footerReady) {
        this.$nextTick(() => {
          this.saveOriginalState();
        });
      }
    },
    async handleGoBack() {
      if (this.hasUnsavedChanges) {
        const answer = await this.ShowCancelModal(
          'Cambios sin Guardar',
          '¿Está seguro que desea salir? Hay cambios sin guardar que se perderán.'
        );
        if (!answer) return;
      }
      this.skipRouteGuard = true;
      this.$router.go(-1);
    },
    validateMalformatedTokens() {
      const quill = this.quillEditorContentRef;
      if (!quill) return;

      const errors = [];
      const stack = [];
      const activeEachBlocks = [];

      // === 1. OBTENER CONTENIDO ===
      const delta = quill.getContents();
      let text = '';

      delta.ops.forEach((op) => {
        if (typeof op.insert === 'string') {
          text += op.insert;
        }
      });

      text = text.replace(/\u00A0/g, ' ');

      // === FUNCIÓN AUXILIAR: Obtener número de línea ===
      const getLineNumber = (position) => {
        return text.substring(0, position).split('\n').length;
      };

      // === 2. ENCONTRAR TOKENS (CON ANIDACIÓN) Y DETECTAR INCOMPLETOS ===
      const tokens = [];
      const incompleteTokens = [];
      let i = 0;

      while (i < text.length) {
        if (text[i] === '{' && text[i + 1] === '{') {
          let depth = 0;
          let start = i;
          let j = i;

          // Buscar el cierre correcto considerando anidación
          while (j < text.length) {
            if (text[j] === '{' && text[j + 1] === '{') {
              depth++;
              j += 2;
            } else if (text[j] === '}' && text[j + 1] === '}') {
              depth--;
              j += 2;
              if (depth === 0) {
                tokens.push({
                  token: text.substring(start, j),
                  start: start,
                  end: j,
                  line: getLineNumber(start),
                });
                i = j;
                break;
              }
            } else {
              j++;
            }
          }

          // Si no se cerró, es un token incompleto
          if (depth > 0) {
            const nextOpen = text.indexOf('{{', start + 2);
            const endPos =
              nextOpen > 0
                ? Math.min(nextOpen, start + 52)
                : Math.min(start + 52, text.length);
            const preview = text.substring(start, endPos);
            const truncated = preview.split('\n')[0];
            const tokenPreview =
              truncated.length < preview.length || endPos < text.length
                ? truncated + '...'
                : truncated;

            incompleteTokens.push({
              preview: tokenPreview,
              line: getLineNumber(start),
              position: start,
            });

            i++;
          }
        } else {
          i++;
        }
      }

      // === 3. REPORTAR TOKENS INCOMPLETOS ===
      incompleteTokens.forEach((incomplete) => {
        errors.push({
          message: `Token incompleto "${incomplete.preview}" (línea ${incomplete.line})`,
        });
      });

      // === 5. CONSTRUIR MAPA INVERSO: Token -> Bloques permitidos ===
      const tokenToBlocks = {};
      Object.entries(restrictedTokens).forEach(([blockName, tokenList]) => {
        tokenList.forEach((tokenName) => {
          if (!tokenToBlocks[tokenName]) {
            tokenToBlocks[tokenName] = [];
          }
          tokenToBlocks[tokenName].push(blockName);
        });
      });

      // === 6. VALIDAR ===
      tokens.forEach(({ token, start, end, line }) => {
        const innerRaw = token.slice(2, -2);
        const inner = innerRaw.replace(/\s+/g, ' ').trim();

        // --- #each ---
        const eachMatch = inner.match(/^#each\s+\{\{(.+?)\}\}/i);
        if (eachMatch) {
          const variable = eachMatch[1].replace(/\s+/g, ' ').trim();
          stack.push({ startIndex: start, variable, line });
          activeEachBlocks.push({ start, end: null, variable });
          return;
        }

        // --- #endeach ---
        if (inner === '#endeach') {
          const last = stack.pop();
          if (!last) {
            errors.push({
              message: `Cierre {{#endeach}} sin apertura (línea ${line})`,
            });
          } else {
            const block = activeEachBlocks.find(
              (b) => b.start === last.startIndex && b.end === null
            );
            if (block) block.end = end;
          }
          return;
        }

        // --- TOKEN RESTRINGIDO ---
        if (tokenToBlocks[inner]) {
          const allowedBlocks = tokenToBlocks[inner];

          const isInside = activeEachBlocks.some((block) => {
            // Verificar si estamos dentro de algún bloque permitido
            if (!allowedBlocks.includes(block.variable)) return false;
            if (block.end !== null && start >= block.end) return false;
            return start > block.start;
          });

          if (!isInside) {
            // Formatear la lista de bloques permitidos
            const allowedList =
              allowedBlocks.length === 1
                ? `{{#each {{${allowedBlocks[0]}}}}}`
                : allowedBlocks.map((v) => `{{#each {{${v}}}}}}`).join(' o ');

            errors.push({
              message: `El token "{{${inner}}}" solo puede usarse dentro de ${allowedList} (línea ${line})`,
            });
          }
        }

        // --- ESPACIOS EXTRA ---
        if (!token.startsWith('{{#') && innerRaw !== inner) {
          errors.push({
            message: `Espacios extra en token: "${token}" (línea ${line})`,
          });
        }
      });

      // === 7. BLOQUES SIN CERRAR ===
      stack.forEach((block) => {
        errors.push({
          message: `Falta {{#endeach}} para {{#each {{${block.variable}}}}} (línea ${block.line})`,
        });
      });

      this.tokenErrors = errors;
    },
  },
};
</script>

<style lang="scss" scoped>
.editor-container {
  display: flex;
  margin-top: 20px;
}

.document-editor-wrapper {
  flex: 1;
  background: #f4f6f3;
  overflow: visible;
  padding-top: 30px;
}

.document-builder {
  background: #fff;
  min-height: calc(100vh - 250px);
  max-width: 25cm;
  margin: 0px auto 30px auto;
  display: flex;
  flex-direction: column;
}

.document-section {
  position: relative;
  border: 2px solid transparent;
  border-radius: 4px;
  padding: 15px;
  transition: all 0.2s ease;

  .section-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 600;
    font-size: 0.85rem;
    color: #3f5b60;
    padding-bottom: 8px;
    background: #f4f6f3;
    padding: 8px 24px;
  }
}

.header-section,
.footer-section {
  background: #fff;
  padding: 5px 5px;
}

.content-section {
  flex: 1;
  padding: 0 6px;
}

.editor-active {
}

.toolbar-actions {
  margin-left: auto;
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  button {
    background: #fff;
    color: #3f5b60;
    border: 1px solid #3f5b60;
    border-radius: 4px;
    width: auto;
    height: 40px;
    border-radius: 4px;
    padding: 0px 15px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    &:hover {
      color: #3f5b60;
    }
    &:disabled {
      cursor: default;
      opacity: 0.5;
      border: 1px solid #9e9e9e;
    }
  }
}

@media (max-width: 992px) {
  .editor-container {
    flex-direction: column;
  }
}

@media (max-width: 768px) {
  .document-builder {
    padding: 20px;
  }
}
</style>

<style lang="scss">
.editor-minimal .ql-container,
.editor-extended .ql-container {
  border: none !important;
}

.editor-extended {
  .ql-container {
    border-width: 0 1px 0 1px;
    border-style: solid;
    border-color: #f4f6f3;
    padding: 10px 30px;
  }
}

.editor-minimal .ql-editor {
  min-height: 80px;
  line-height: 1.6;
  border-width: 0 1px 0 1px;
  border-style: solid;
  border-color: #f4f6f3;
  background: #fff;
  min-height: 50px;
  padding: 0px 17px;
}

.editor-minimal {
  .ql-disabled {
    border: 1px solid #f4f6f3 !important;
    padding-top: 5px;
  }
}

.editor-minimal[contenteditable='false'] {
  border-width: 1px 1px 0 1px !important;
  padding-top: 5px !important;
}

.editor-extended .ql-editor {
  min-height: calc(100vh - 400px);
}

.token-value {
  font-size: 14px;
  font-style: italic;
  font-weight: 700;
  display: inline-block;
  padding: 2px 0px;
  border-radius: 4px;
  background-color: #f3f4f6;
  border: 1px dashed #bdbdbd;
  cursor: default;
  user-select: none;
  &--string {
    color: #f3af54;
  }
  &--number {
    color: #2db782;
  }
  &--datetime {
    color: #6154f3;
  }
  &--boolean {
    color: #2980b9;
  }
}

.editor-minimal .ql-toolbar,
.editor-extended .ql-toolbar {
  border: unset !important;
}

.editor-minimal {
  .ql-toolbar {
    border-width: 1px 1px 0 1px !important;
    border-style: solid !important;
    border-color: #f4f6f3 !important;
    border-radius: 4px 4px 0 0 !important;
  }
}

#contentEditor {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid #ddd;
  border-radius: 4px;
  .ql-toolbar {
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 10;
    border-bottom: 1px solid #ccc;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
  .ql-container {
    flex: 1;
    background: #fff;
    padding: 10px 30px;
  }
}

#contentEditor {
  white-space: pre;
}

.ql-editor {
  white-space: normal !important;
}

.token-error-panel {
  z-index: 10;
  padding: 10px;
  background-color: #ffe6e6;
  color: #ff5b5c;
}
</style>
